import { AnimatePresence, animate } from 'framer-motion';
import moment from 'moment';
import { type KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useFeature, useLocalize } from '@treams/treams-hooks';
import { PreviewIcon, SearchIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { Avatar, Button, Input, tokens } from '@treams/treams-ui';

import useScoreOptions from '../hooks/useScoreOptions';
import {
	type SidebarEntry,
	type SidebarRowStatus,
	isIncompleteOnViewedDay,
	isOutdated,
	nextSidebarWork,
	pinSidebarEntry,
	releaseIncompleteIfComplete,
	sidebarRowStatuses,
	sortSidebarEntries,
} from '../talentReviewSidebarOrder';
import TalentReviewSidebarCard, {
	type TalentReviewSidebarCardMode,
} from './TalentReviewSidebarCard';
import TalentReviewSidebarLayout from './TalentReviewSidebarLayout';
import TalentReviewSidebarSlot from './TalentReviewSidebarSlot';
import TalentReviewSidebarStatus from './TalentReviewSidebarStatus';

const SidebarTitle = styled.h2`
	margin: 0;
	color: ${tokens.text.primary};
	font-family: ${tokens.typography.fontFamily};
	font-size: 18px;
	font-weight: ${tokens.typography.weights.medium};
	line-height: ${tokens.typography.lineHeights.heading};
`;

const Explainer = styled.p`
	margin: 0;
	color: ${tokens.text.secondary};
	font-family: ${tokens.typography.fontFamily};
	font-size: ${tokens.typography.sizes.body}px;
	line-height: ${tokens.typography.lineHeights.body};
`;

// Room kept between the open card and the list edges when scrolling it into
// view; the fades cover the edges.
const SCROLL_MARGIN = 32;

// Search is only useful once the list is long enough to scan.
const SEARCH_MIN_COUNT = 6;

const MemberCard = styled(Box)`
	width: 100%;
	box-sizing: border-box;
	cursor: pointer;
	outline: none;
`;

const MemberRow = styled(Flex)`
	align-items: center;
	gap: ${tokens.gap.list}px;
`;

const MemberName = styled.div`
	color: ${tokens.text.primary};
	font-family: ${tokens.typography.fontFamily};
	font-size: ${tokens.typography.sizes.body}px;
	font-weight: ${tokens.typography.weights.semibold};
	line-height: ${tokens.typography.lineHeights.body};
`;

// Keys pressed inside a control belong to that control (caret movement,
// select option navigation), not to the list. The rows are options too
// (`data-user-id`); keys on a row itself do belong to the list.
const isInsideControl = (event: KeyboardEvent) => {
	const control = (event.target as HTMLElement | null)?.closest(
		'textarea, input, select, [role="combobox"], [role="option"], [role="menu"]',
	);
	return !!control && !control.hasAttribute('data-user-id');
};

/**
 * The people on the grid, with the card for the selected person. Today's
 * incomplete work comes first, then outdated people, then the rest A–Z. The
 * groups are fixed for the day, so reviewing someone does not shuffle the
 * list under the pointer. People not on the grid are not listed; they live
 * in the "unplaced" card above the grid. Selecting a person grows their row
 * into their card, in place, and "next" steps down the list the way it is
 * shown.
 * @param props component props
 * @param props.localDate the day shown; per-day state resets when it changes
 * @param props.template the grid template (cell labels, outdated threshold)
 * @param props.placements the current placements
 * @param props.selectedUserID the selected person
 * @param props.movedUserIDs users moved this session, in move order
 * @param props.readonly no editing: stored-review cards only
 * @param props.focusKey bump to focus the card's textarea when it is empty
 * @param props.onSelectUser isolate a person, or nobody
 * @param props.onHoverUser a row is hovered (or left: undefined); the grid rings that person when nobody has an open card
 * @param props.onRequestFocus ask the card to focus its textarea
 * @param props.onUpdateMotivation fires per keystroke; saving is debounced upstream
 * @param props.onFlushMotivation save a pending motivation right away
 * @param props.onUpdatePlacement persist a placement change (score)
 * @param props.onMovePlacement move a person to another cell
 * @param props.onUndoMove move a person back to where they came from
 */
export default function TalentReviewSidebar(props: {
	localDate: graphql.Scalars['LocalDate']['output'];
	template: graphql.TalentReviewTemplate;
	placements: graphql.TalentReviewGridPlacement[];
	selectedUserID?: string;
	movedUserIDs: string[];
	readonly?: boolean;
	focusKey: number;
	onSelectUser: (userID?: string) => void;
	onHoverUser?: (userID?: string) => void;
	onRequestFocus: () => void;
	onUpdateMotivation: (userID: string, motivation: string) => void;
	onFlushMotivation: (userID: string) => void;
	onUpdatePlacement: (placement: graphql.TalentReviewGridPlacement) => void;
	onMovePlacement: (userID: string, row: number, column: number) => void;
	onUndoMove: (userID: string) => void;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const [query, setQuery] = useState('');
	const listRef = useRef<HTMLDivElement>(null);
	const prevSelectedRef = useRef(props.selectedUserID);
	const pinRef = useRef<{ userID: string; index: number }>();
	const [scrolledFromTop, setScrolledFromTop] = useState(false);
	const hasScores = useFeature(graphql.FeatureName.TalentReviewsScores);
	const scoreOptions = useScoreOptions();
	const outdatedAfterDays = props.template.outdatedAfterDays;

	// Incomplete is frozen add-only, except the open card: a dropdown or drop
	// must not join the work queue until the card is left. Leaving a completed
	// card (next, close, another row) drops them from the group. The orange
	// marker itself is live.
	const incompleteNow = () =>
		new Set(
			props.placements
				.filter((placement) =>
					isIncompleteOnViewedDay(placement, {
						hasScores,
						localDate: props.localDate,
					}),
				)
				.map((placement) => placement.user.id),
		);
	const outdatedNow = () =>
		new Set(
			props.placements
				.filter((placement) => isOutdated(placement, outdatedAfterDays))
				.map((placement) => placement.user.id),
		);
	const [incompleteAtLoad, setIncompleteAtLoad] = useState(incompleteNow);
	const [outdatedAtLoad, setOutdatedAtLoad] = useState(outdatedNow);

	// The sidebar stays mounted while switching days (rows slide in and out),
	// so per-day state is reset here instead of by remounting.
	const [stateDate, setStateDate] = useState(props.localDate);
	if (stateDate !== props.localDate) {
		setStateDate(props.localDate);
		setIncompleteAtLoad(incompleteNow());
		setOutdatedAtLoad(outdatedNow());
		setQuery('');
		pinRef.current = undefined;
	}

	const liveIncomplete = incompleteNow();
	let incompleteUserIDs = incompleteAtLoad;
	for (const userID of liveIncomplete) {
		if (userID === props.selectedUserID || incompleteUserIDs.has(userID)) {
			continue;
		}
		if (incompleteUserIDs === incompleteAtLoad) {
			incompleteUserIDs = new Set(incompleteAtLoad);
		}
		incompleteUserIDs.add(userID);
	}
	if (incompleteUserIDs !== incompleteAtLoad) {
		setIncompleteAtLoad(incompleteUserIDs);
	}

	useEffect(() => {
		const previous = prevSelectedRef.current;
		prevSelectedRef.current = props.selectedUserID;
		if (!previous || previous === props.selectedUserID) {
			return;
		}

		const placement = props.placements.find((item) => item.user.id === previous);
		const stillIncomplete = placement
			? isIncompleteOnViewedDay(placement, {
					hasScores,
					localDate: props.localDate,
				})
			: false;

		setIncompleteAtLoad((current) =>
			releaseIncompleteIfComplete(current, previous, stillIncomplete),
		);
	}, [props.selectedUserID, props.placements, hasScores, props.localDate]);

	const natural = useMemo(
		() =>
			sortSidebarEntries(props.placements, {
				incompleteUserIDs,
				outdatedUserIDs: outdatedAtLoad,
				movedUserIDs: props.movedUserIDs,
				holdUserID: props.selectedUserID,
			}),
		[props.placements, incompleteUserIDs, outdatedAtLoad, props.movedUserIDs, props.selectedUserID],
	);

	if (props.selectedUserID !== pinRef.current?.userID) {
		if (!props.selectedUserID) {
			pinRef.current = undefined;
		} else {
			const index = natural.findIndex((entry) => entry.user.id === props.selectedUserID);
			pinRef.current = index === -1 ? undefined : { userID: props.selectedUserID, index };
		}
	}

	const ordered = pinSidebarEntry(natural, pinRef.current);

	// A hidden search field must not keep filtering: there would be no way to
	// clear it.
	const searchable = ordered.length >= SEARCH_MIN_COUNT;
	if (!searchable && query) {
		setQuery('');
	}
	const activeQuery = searchable ? query : '';

	// Rows slide for grid and day changes; a new search just filters.
	const shownQueryRef = useRef(activeQuery);
	const slideRows = activeQuery === shownQueryRef.current;
	useEffect(() => {
		shownQueryRef.current = activeQuery;
	}, [activeQuery]);

	const visible = useMemo(() => {
		const needle = activeQuery.trim().toLowerCase();
		if (!needle) {
			return ordered;
		}

		return ordered.filter(({ user }) =>
			`${user.firstName} ${user.lastName}`.toLowerCase().includes(needle),
		);
	}, [ordered, activeQuery]);

	useEffect(() => {
		const list = listRef.current;
		if (!list) {
			return;
		}

		const updateFade = () => setScrolledFromTop(list.scrollTop > 0);
		updateFade();
		list.addEventListener('scroll', updateFade, { passive: true });

		const observer = new ResizeObserver(updateFade);
		observer.observe(list);

		return () => {
			list.removeEventListener('scroll', updateFade);
			observer.disconnect();
		};
	}, []);

	// The selected person's card replaces their row, in place: the list keeps
	// the order it shows, and "next" and the arrow keys both step down it.
	const sortOptions = {
		incompleteUserIDs,
		outdatedUserIDs: outdatedAtLoad,
		movedUserIDs: props.movedUserIDs,
		holdUserID: props.selectedUserID,
	};
	const nextWork = props.selectedUserID
		? nextSidebarWork(visible, props.selectedUserID, sortOptions)
		: undefined;

	// Once per selection, after the card has grown in place (0.3s), scroll it
	// into view when it ended up (partly) hidden. Measuring earlier would see
	// the list before the card took its room. A card taller than the list is
	// aligned to the top instead of pulled up by its bottom edge.
	const revealFor = useRef<string>();
	useEffect(() => {
		revealFor.current = props.selectedUserID;
	}, [props.selectedUserID]);

	const revealSelected = () => {
		const list = listRef.current;
		if (!list || !revealFor.current || revealFor.current !== props.selectedUserID) {
			return;
		}
		revealFor.current = undefined;

		const item = props.selectedUserID
			? list.querySelector<HTMLElement>(`[data-user-id="${props.selectedUserID}"]`)
			: null;
		if (!item) {
			return;
		}

		const listRect = list.getBoundingClientRect();
		const rect = item.getBoundingClientRect();
		const above = rect.top - (listRect.top + SCROLL_MARGIN);
		const below = rect.bottom - (listRect.bottom - SCROLL_MARGIN);
		const tallerThanList = rect.height > listRect.height - SCROLL_MARGIN * 2;
		const delta = above < 0 || tallerThanList ? above : below > 0 ? below : 0;
		if (Math.abs(delta) < 1) {
			return;
		}

		const from = list.scrollTop;
		animate(from, from + delta, {
			duration: 0.3,
			ease: [0.22, 1, 0.36, 1],
			onUpdate: (value) => {
				list.scrollTop = value;
			},
		});
	};

	const cardMode: TalentReviewSidebarCardMode = props.readonly ? 'display' : 'edit';

	const cellLabel = (row: number, column: number) => {
		const label = localize(props.template.cells[row]?.[column]?.label)?.text;
		if (label) {
			return label;
		}

		const x = localize(props.template.xAxis.ticks[column])?.text ?? '';
		const y = localize(props.template.yAxis.ticks[row])?.text ?? '';
		return `${x} · ${y}`;
	};

	const rowMeta = (placement: graphql.TalentReviewGridPlacement, statuses: SidebarRowStatus[]) =>
		statuses.map((status) => {
			if (status === 'noMotivation') {
				return {
					status,
					tone: 'warning' as const,
					text: t('talentReviews.v2.sidebar.card.noMotivation'),
				};
			}

			if (status === 'noScore') {
				return {
					status,
					tone: 'warning' as const,
					text: t('talentReviews.v2.sidebar.row.noScore', { defaultValue: 'No score yet' }),
				};
			}

			if (status === 'noMotivationAndScore') {
				return {
					status,
					tone: 'warning' as const,
					text: t('talentReviews.v2.sidebar.row.noMotivationAndScore', {
						defaultValue: 'No motivation or score yet',
					}),
				};
			}

			if (status === 'moved') {
				return {
					status,
					tone: 'info' as const,
					text: t('talentReviews.v2.sidebar.row.moved', {
						defaultValue: 'Moved to {{cell}}',
						cell: cellLabel(placement.row, placement.column),
					}),
				};
			}

			if (status === 'outdated') {
				return {
					status,
					tone: 'warning' as const,
					text: t('talentReviews.v2.sidebar.row.outdated', { defaultValue: 'Outdated' }),
				};
			}

			return {
				status,
				tone: undefined,
				text: t('talentReviews.v2.sidebar.row.reviewed', {
					defaultValue: 'Last review {{time}}',
					time: moment(placement.createdAt).fromNow(),
				}),
			};
		});

	// Focus the open card (its root, or the textarea once the card's own
	// effect runs), skipping content that is fading out.
	const focusCard = (userID: string) => {
		const slot = listRef.current?.querySelector<HTMLElement>(
			`[data-user-id="${userID}"] [data-slot-content]`,
		);
		const target = Array.from(slot?.querySelectorAll<HTMLElement>('[tabindex]') ?? []).find(
			(element) => !element.closest('[data-motion-pop-id]'),
		);
		target?.focus({ preventScroll: true });
	};

	const selectAt = (index: number) => {
		const entry = visible[index];
		if (!entry) {
			return;
		}

		props.onSelectUser(entry.user.id);
		requestAnimationFrame(() => focusCard(entry.user.id));
	};

	// "Next": flush the motivation and continue with the next work item. The
	// last last-review jumps to remaining missing work; otherwise the list as
	// shown. At the end the card closes; no wrap into the move dump.
	const handleNext = (userID: string) => {
		props.onFlushMotivation(userID);
		props.onSelectUser(nextWork?.user.id);
		if (nextWork) {
			props.onRequestFocus();
		}
	};

	const handleListKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
		if (event.key === 'Escape') {
			if (props.selectedUserID) {
				event.preventDefault();
				props.onSelectUser(undefined);
			}
			return;
		}

		if (isInsideControl(event)) {
			return;
		}

		if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
			event.preventDefault();
			const index = visible.findIndex(({ user }) => user.id === props.selectedUserID);
			const step = event.key === 'ArrowDown' ? 1 : -1;
			const target = index === -1 ? (step === 1 ? 0 : visible.length - 1) : index + step;
			selectAt(Math.max(0, Math.min(visible.length - 1, target)));
		}
	};

	const handleRowKeyDown = (event: KeyboardEvent, onSelect: () => void) => {
		if (event.key !== 'Enter' && event.key !== ' ') {
			return;
		}

		event.preventDefault();
		onSelect();
	};

	return (
		<TalentReviewSidebarLayout
			listRef={listRef}
			scrolledFromTop={scrolledFromTop}
			onListKeyDown={handleListKeyDown}
			header={
				<>
					<SidebarTitle>{t('talentReviews.v2.sidebar.title')}</SidebarTitle>
					<Box mt={2}>
						<Explainer>
							{t('talentReviews.v2.sidebar.description', {
								defaultValue: 'Click a team member to open their talent review.',
							})}
						</Explainer>
					</Box>
					<Box mt={3}>
						<Button
							type="secondary"
							width="fit-content"
							icon={<PreviewIcon />}
							title={t('talentReviews.v2.sidebar.viewAll')}
							disabled={!props.selectedUserID}
							onClick={() => props.onSelectUser(undefined)}
						/>
					</Box>
					{searchable && (
						<Box mt={3}>
							<Input
								icon={<SearchIcon />}
								value={query}
								placeholder={t('talentReviews.v2.sidebar.searchPlaceholder')}
								onChange={setQuery}
							/>
						</Box>
					)}
				</>
			}
		>
			{visible.length === 0 && (
				<Explainer>
					{activeQuery.trim()
						? t('talentReviews.v2.index.notFound')
						: t('talentReviews.v2.sidebar.empty')}
				</Explainer>
			)}
			{/* Rows slide in from the right and out to the right (see the slot).
			    The selected person's row grows into their card, in place, so the
			    list keeps the order it shows. */}
			<AnimatePresence initial={false} custom={slideRows}>
				{visible.map(({ user, placement }: SidebarEntry) => {
					const outdated = isOutdated(placement, outdatedAfterDays);
					const handleSelect = () => props.onSelectUser(user.id);
					const open = user.id === props.selectedUserID;
					const movedThisSession = props.movedUserIDs.includes(user.id);
					const statuses = sidebarRowStatuses(placement, {
						outdated,
						movedThisSession,
						hasScores,
						localDate: props.localDate,
					});
					const meta = rowMeta(placement, statuses);

					return (
						<TalentReviewSidebarSlot
							key={user.id}
							userID={user.id}
							contentKey={open ? `card-${cardMode}` : 'row'}
							raised={open}
							slide={slideRows}
							onSettled={open ? revealSelected : undefined}
							onMouseEnter={() => props.onHoverUser?.(user.id)}
							onMouseLeave={() => props.onHoverUser?.(undefined)}
						>
							{open ? (
								<TalentReviewSidebarCard
									placement={placement}
									template={props.template}
									mode={cardMode}
									movedThisSession={movedThisSession}
									last={!nextWork}
									outdated={outdated}
									hasScores={hasScores}
									scoreOptions={scoreOptions}
									statusLines={meta}
									focusKey={props.focusKey}
									onChangeMotivation={(motivation) => props.onUpdateMotivation(user.id, motivation)}
									onChangeScore={(score) => props.onUpdatePlacement({ ...placement, score })}
									onChangeRetentionRisk={(retentionRisk) =>
										props.onUpdatePlacement({
											...placement,
											retentionRisk: retentionRisk as graphql.TalentReviewRetentionRisk | null,
										})
									}
									onChangePosition={(row, column) => props.onMovePlacement(user.id, row, column)}
									onNext={() => handleNext(user.id)}
									onUndo={() => props.onUndoMove(user.id)}
									onClose={() => props.onSelectUser(undefined)}
								/>
							) : (
								<MemberCard
									p={2}
									role="button"
									tabIndex={0}
									onClick={handleSelect}
									onKeyDown={(event) => handleRowKeyDown(event, handleSelect)}
								>
									<MemberRow>
										<Avatar.Ring tone={outdated ? 'warning' : undefined}>
											<Avatar size={36} user={user} />
										</Avatar.Ring>
										<Box minWidth={0}>
											<MemberName>
												{user.firstName} {user.lastName}
											</MemberName>
											<TalentReviewSidebarStatus lines={meta} />
										</Box>
									</MemberRow>
								</MemberCard>
							)}
						</TalentReviewSidebarSlot>
					);
				})}
			</AnimatePresence>
		</TalentReviewSidebarLayout>
	);
}
